/**
 * Generic node-graph model.
 *
 * Domain-agnostic data types describing a node graph that can host *both*
 * dataflow graphs (TSL shader graphs, MaterialX) and control-flow / event
 * graphs (glTF `KHR_interactivity`). The single most important unifying
 * decision is that data edges and flow edges are kept as two separate lists
 * with distinct anchoring:
 *
 * - **data edges** are anchored on the *consumer* and point backward to the
 *   producer's output (pull model — matches MaterialX `nodename`, KHR `values`,
 *   and TSL node composition).
 * - **flow edges** are anchored on the *producer* and point forward to the next
 *   node's flow input (push model — matches KHR `flows`). Dataflow-only graphs
 *   simply leave `flowEdges` empty.
 *
 * Nothing in this module depends on three.js or any UI framework. Domain
 * adapters convert their native representation into this model; see `adapter.ts`.
 */

// #region Data types

/**
 * Coarse classification of a data socket's value type. The union covers every
 * category that appears across the target domains: numeric scalars/vectors/
 * matrices (TSL + MaterialX + KHR), object references (`ref`, KHR), strings and
 * arrays (MaterialX + KHR config), and opaque shader closures
 * (`surfaceshader` / BSDF / EDF / VDF, MaterialX only).
 */
export type DataTypeCategory =
    | "scalar"
    | "vector"
    | "matrix"
    | "ref"
    | "string"
    | "array"
    | "shaderClosure"
    | "unknown";

/** A data socket's value type. */
export interface DataType {
    /** Canonical signature, e.g. `"float"`, `"vec3"`, `"mat4"`, `"color3"`, `"surfaceshader"`. */
    signature: string;
    category: DataTypeCategory;
    /** Component count for scalar/vector/matrix types (1, 2, 3, 4, 9, 16, …). */
    components?: number;
}

// #region Sockets

/**
 * Discriminates the two socket kinds. Only `data` sockets carry a {@link DataType};
 * `flow` sockets are untyped control-flow (execution) connection points.
 */
export type SocketKind = "data" | "flow";

/** A concrete input or output connection point on a {@link GraphNode}. */
export interface Socket {
    id: string;
    name: string;
    kind: SocketKind;
    /** Present only for `data` sockets. */
    type?: DataType;
    /** Optional hover text, e.g. "Connected to <node>.<slot>". */
    tooltip?: string;
}

// #region Node catalog (NodeType)

/** Declares a socket on a {@link NodeType} (the catalog / palette entry). */
export interface NodeTypeSocket {
    name: string;
    kind: SocketKind;
    type?: DataType;
}

/**
 * A static configuration parameter on a {@link NodeType}. Configuration uses a
 * deliberately separate, smaller type system than data sockets — KHR allows
 * `string` / `int[]` here that value sockets do not.
 */
export interface ConfigParam {
    name: string;
    type: "bool" | "int" | "int[]" | "float" | "string";
    defaultValue?: unknown;
}

/**
 * A catalog entry: the *type* of a node (analogous to a MaterialX `nodedef`, a
 * KHR `declaration`, or a TSL node factory). This is the pivot that lets every
 * domain load its node catalog into one palette.
 */
export interface NodeType {
    /** Operation identifier, e.g. `"math/add"`, `"multiply"`, `"OperatorNode"`. */
    op: string;
    /** Domain namespace, e.g. `"tsl"`, `"materialx"`, `"gltf-interactivity"`. */
    domain: string;
    /** UI grouping, e.g. `"math"`, `"flow"`, `"shader"`, `"event"`, `"input"`. */
    category: string;
    inputs: NodeTypeSocket[];
    outputs: NodeTypeSocket[];
    config?: ConfigParam[];
}

// #region Nodes & edges

/** An instance of a node in a graph. */
export interface GraphNode {
    id: string;
    /** Native property segments from the graph owner; absent for generated nodes. */
    sourcePath?: readonly string[];
    /** References a {@link NodeType} `op`; free-form when the domain has no catalog entry. */
    typeRef: string;
    title: string;
    /** UI grouping, mirrors {@link NodeType.category}; used for coloring/palette. */
    category?: string;
    /** Static parameters baked into the node (KHR `configuration`, MaterialX tokens). */
    config?: Record<string, unknown>;
    inputs: Socket[];
    outputs: Socket[];
    /** Assigned by {@link layout}; absent in the raw model. */
    position?: Point;
    /** Set when this node instantiates a subgraph/group ({@link NodeGraph.id}). */
    subgraphRef?: string;
    /**
     * Free-form, domain-specific metadata (value previews, source `uuid`s, etc.).
     * The core never interprets this; adapters and renderers may.
     */
    meta?: Record<string, unknown>;
}

/** A `{ node, socket }` reference to one endpoint of an edge. */
export interface SocketEndpoint {
    node: string;
    socket: string;
}

/**
 * A dataflow connection. `to` (the consumer input) is the anchor; `from` is the
 * producer output feeding it.
 */
export interface DataEdge {
    from: SocketEndpoint;
    to: SocketEndpoint;
}

/**
 * A control-flow (execution) connection. `from` (the producer flow-out) is the
 * anchor; `to` is the next node's flow input.
 */
export interface FlowEdge {
    from: SocketEndpoint;
    to: SocketEndpoint;
}

// #region Graph-level extras (KHR-only)

/** A graph variable (glTF `KHR_interactivity`; empty for TSL / MaterialX). */
export interface GraphVariable {
    id: string;
    name: string;
    type?: DataType;
    value?: unknown;
}

/** A (custom) event declaration (glTF `KHR_interactivity`). */
export interface GraphEvent {
    id: string;
    name: string;
    /** Payload value sockets carried by the event. */
    values?: Socket[];
}

// #region Graph

export interface Point {
    x: number;
    y: number;
}

/** A complete node graph in the generic model. */
export interface NodeGraph {
    id: string;
    name?: string;
    /** Source type of the graph, e.g. the material's class ("MeshStandardNodeMaterial"). */
    type?: string;
    /** Originating domain, e.g. `"tsl"`. */
    domain: string;
    nodes: GraphNode[];
    dataEdges: DataEdge[];
    /** Control-flow edges. Empty for pure-dataflow domains (TSL, MaterialX). */
    flowEdges: FlowEdge[];
    /** Present only for domains with variables (KHR). */
    variables?: GraphVariable[];
    /** Present only for domains with events (KHR). */
    events?: GraphEvent[];
    /** Named subgraphs/groups that nodes may reference via {@link GraphNode.subgraphRef}. */
    subgraphs?: NodeGraph[];
}

// #region Type-color hook

/**
 * Maps a data-socket type to a CSS color for rendering. Supplied per-domain by
 * an adapter (see {@link GraphAdapter.typeColor}); the core ships a neutral
 * fallback in {@link defaultTypeColor}.
 */
export type TypeColorFn = (type: DataType | undefined) => string;

/** Neutral fallback coloring keyed on {@link DataTypeCategory}. */
export function defaultTypeColor(type: DataType | undefined): string {
    switch (type?.category) {
        case "scalar": return "#9aa0a6";
        case "vector": return "#4fc3f7";
        case "matrix": return "#ba68c8";
        case "ref": return "#ffb74d";
        case "string": return "#81c784";
        case "array": return "#4db6ac";
        case "shaderClosure": return "#f06292";
        default: return "#78909c";
    }
}

// #region Small helpers

/** Creates an empty graph for a domain. */
export function createGraph(id: string, domain: string, name?: string, type?: string): NodeGraph {
    return { id, name, type, domain, nodes: [], dataEdges: [], flowEdges: [] };
}

/** Returns the node with the given id, or `undefined`. */
export function findNode(graph: NodeGraph, id: string): GraphNode | undefined {
    return graph.nodes.find(n => n.id === id);
}

/** Returns the socket with the given id on a node, or `undefined`. */
export function findSocket(node: GraphNode, socketId: string): Socket | undefined {
    return node.inputs.find(s => s.id === socketId) ?? node.outputs.find(s => s.id === socketId);
}
